/**
 * ExternalTasksView - Displays tasks from external systems (e.g., Filevine)
 *
 * This component will handle:
 * - Fetching external tasks from the API
 * - Displaying external tasks in a table format
 * - Managing external task interactions
 *
 * Future functionality:
 * - Linking external tasks to internal tasks
 * - Syncing external task updates
 * - Filtering and searching external tasks
 */

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDebounce } from '@uidotdev/usehooks';
import {
  Bookmark,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BulkCreateDialog } from '@/components/external-task-bulk-create';
import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { Input } from '@/components/ui/catalyst/input';
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  EXTERNAL_TASK_STATUS_COLORS,
  EXTERNAL_TASK_STATUSES,
} from '@/constants/external-task-status';
import { useExternalTaskFilters } from '@/hooks/useExternalTaskFilters';
import { useSavedFilters } from '@/hooks/useSavedFilters';
import { cn } from '@/lib/utils';
import { externalTasksApiClient } from '@/services/api/externalTaskService';
import type {
  ExternalTaskListItemSchema,
  ExternalTaskStatus,
} from '@/services/api/generated/externalTaskService';
import { enhancedApi as externalTaskApi } from '@/services/api/generated/externalTaskService';

import { BulkLinkDialog } from './BulkLinkDialog';
import { ColumnHeaderFilter } from './ColumnHeaderFilter';
import { formatDate } from './dateUtils';
import { ExternalTaskDetail } from './ExternalTaskDetail';
import { RuleMatchCell, SlaStatusCell } from './RuleMatchCells';
import { SaveFilterDialog } from './SaveFilterDialog';

interface Option {
  id: string;
  name: string;
}

export const ExternalTasksView: React.FC = () => {
  const navigate = useNavigate();
  const { external_task_id } = useParams<{ external_task_id?: string }>();

  // Use URL param as the selected task ID
  const selectedTaskId = external_task_id || null;

  // Bulk selection state
  const [selectedRows, setSelectedRows] = React.useState<Set<string>>(
    new Set(),
  );
  const [lastClickedIndex, setLastClickedIndex] = React.useState<number | null>(
    null,
  );

  // Bulk link dialog state
  const [isBulkLinkDialogOpen, setIsBulkLinkDialogOpen] = React.useState(false);

  // Bulk create dialog state
  const [isBulkCreateDialogOpen, setIsBulkCreateDialogOpen] =
    React.useState(false);

  // Save filter dialog state
  const [isSaveFilterDialogOpen, setIsSaveFilterDialogOpen] =
    React.useState(false);

  // Filter state with URL persistence
  const [filters, filterActions] = useExternalTaskFilters();

  // Saved filters
  const { savedFilters, saveFilter, deleteFilter } = useSavedFilters();
  const {
    searchInput,
    statusFilter,
    sourceFilter,
    firmFilter,
    matterFilter,
    assigneeFilter,
    needsUpdateFilter,
    linkedFilter,
    ordering,
    lastActivityAfter,
    lastActivityBefore,
    hasMatchedRuleFilter,
    hideExcludedFilter,
    slaStatusFilter,
  } = filters;
  const {
    setSearchInput,
    setStatusFilter,
    setSourceFilter,
    setFirmFilter,
    setMatterFilter,
    setAssigneeFilter,
    setNeedsUpdateFilter,
    setLinkedFilter,
    setOrdering,
    setLastActivityAfter,
    setLastActivityBefore,
    setHasMatchedRuleFilter,
    setHideExcludedFilter,
    setSlaStatusFilter,
    setAllFilters,
    clearAllFilters,
  } = filterActions;

  const [searchTerms, setSearchTerms] = React.useState<string[]>(
    searchInput ? searchInput.split(',').filter(Boolean) : [],
  );
  const [currentSearchInput, setCurrentSearchInput] = React.useState('');

  const [assigneeTerms, setAssigneeTerms] = React.useState<string[]>(
    assigneeFilter ? assigneeFilter.split(',').filter(Boolean) : [],
  );
  const [currentAssigneeInput, setCurrentAssigneeInput] = React.useState('');

  // Sync searchTerms when searchInput changes from URL
  React.useEffect(() => {
    const termsFromInput = searchInput
      ? searchInput.split(',').filter(Boolean)
      : [];

    // Build expected string including current input
    const allCurrentTerms = [
      ...searchTerms,
      ...(currentSearchInput.trim() ? [currentSearchInput.trim()] : []),
    ];
    const currentTermsString = allCurrentTerms.join(',');

    // Only sync if the change came from external source (not from our own update)
    if (searchInput !== currentTermsString) {
      // Check if the last term matches current input - if so, exclude it
      const lastTerm = termsFromInput[termsFromInput.length - 1];
      const newTerms =
        lastTerm === currentSearchInput.trim() && currentSearchInput.trim()
          ? termsFromInput.slice(0, -1)
          : termsFromInput;

      setSearchTerms(newTerms);

      // Clear current input when syncing from URL and no match
      if (searchInput === '') {
        setCurrentSearchInput('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Update searchInput when searchTerms or currentSearchInput change
  React.useEffect(() => {
    const allTerms = [
      ...searchTerms,
      ...(currentSearchInput.trim() ? [currentSearchInput.trim()] : []),
    ];
    const newSearchInput = allTerms.join(',');
    if (newSearchInput !== searchInput) {
      setSearchInput(newSearchInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerms, currentSearchInput]);

  // Sync assigneeTerms when assigneeFilter changes from URL
  React.useEffect(() => {
    const termsFromInput = assigneeFilter
      ? assigneeFilter.split(',').filter(Boolean)
      : [];

    // Build expected string including current input
    const allCurrentTerms = [
      ...assigneeTerms,
      ...(currentAssigneeInput.trim() ? [currentAssigneeInput.trim()] : []),
    ];
    const currentTermsString = allCurrentTerms.join(',');

    // Only sync if the change came from external source (not from our own update)
    if (assigneeFilter !== currentTermsString) {
      // Check if the last term matches current input - if so, exclude it
      const lastTerm = termsFromInput[termsFromInput.length - 1];
      const newTerms =
        lastTerm === currentAssigneeInput.trim() && currentAssigneeInput.trim()
          ? termsFromInput.slice(0, -1)
          : termsFromInput;

      setAssigneeTerms(newTerms);

      // Clear current input when syncing from URL and no match
      if (assigneeFilter === '') {
        setCurrentAssigneeInput('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeFilter]);

  // Update assigneeFilter when assigneeTerms or currentAssigneeInput change
  React.useEffect(() => {
    const allTerms = [
      ...assigneeTerms,
      ...(currentAssigneeInput.trim() ? [currentAssigneeInput.trim()] : []),
    ];
    const newAssigneeFilter = allTerms.join(',');
    if (newAssigneeFilter !== assigneeFilter) {
      setAssigneeFilter(newAssigneeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeTerms, currentAssigneeInput]);

  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedAssigneeFilter = useDebounce(assigneeFilter, 300);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  // Handle row click to open detail modal
  const handleRowClick = React.useCallback(
    (task: ExternalTaskListItemSchema) => {
      navigate(`/external-tasks/${task.id}`);
    },
    [navigate],
  );

  // Handle closing detail modal
  const handleCloseDetail = React.useCallback(() => {
    navigate('/external-tasks');
  }, [navigate]);

  // Build query params
  const queryParams = React.useMemo(() => {
    const searchArray = debouncedSearch
      ? debouncedSearch.split(',').filter(Boolean)
      : undefined;
    const assigneeArray = debouncedAssigneeFilter
      ? debouncedAssigneeFilter.split(',').filter(Boolean)
      : undefined;

    return {
      search: searchArray,
      status: statusFilter.length > 0 ? statusFilter : undefined,
      source: sourceFilter.length > 0 ? sourceFilter : undefined,
      firm: firmFilter.length > 0 ? firmFilter : undefined,
      matter: matterFilter.length > 0 ? matterFilter : undefined,
      assigneeName: assigneeArray,
      needsUpdate: needsUpdateFilter || undefined,
      linkedTasksMin: linkedFilter === 'linked' ? 1 : undefined,
      linkedTasksMax: linkedFilter === 'unlinked' ? 0 : undefined,
      ordering: ordering || undefined,
      lastActivityAfter: lastActivityAfter || undefined,
      lastActivityBefore: lastActivityBefore || undefined,
      hasMatchedRule: hasMatchedRuleFilter ?? undefined,
      isExcluded: hideExcludedFilter ? false : undefined,
      slaStatus: slaStatusFilter.length > 0 ? slaStatusFilter : undefined,
    };
  }, [
    debouncedSearch,
    statusFilter,
    sourceFilter,
    firmFilter,
    matterFilter,
    debouncedAssigneeFilter,
    needsUpdateFilter,
    linkedFilter,
    ordering,
    lastActivityAfter,
    lastActivityBefore,
    hasMatchedRuleFilter,
    hideExcludedFilter,
    slaStatusFilter,
  ]);

  // Fetch filter options from API
  const { data: filterOptions } =
    externalTaskApi.useExternalTaskFilterOptionsQuery();

  // Fetch external tasks with infinite query
  const {
    data: pages,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = externalTasksApiClient.useGetExternalTasksInfiniteInfiniteQuery(
    queryParams,
    {
      initialPageParam: {
        offset: 0,
        limit: 50,
      },
    },
  );

  // Flatten paginated data into single array
  const tasks = React.useMemo(() => {
    if (!pages) return [];
    return pages.pages.flatMap((page) => page.results);
  }, [pages]);

  const totalCount = pages?.pages?.[0]?.count || 0;

  // Handle selection changes
  const handleToggleRow = React.useCallback(
    (taskId: string, index: number, shiftKey: boolean) => {
      if (shiftKey && lastClickedIndex !== null) {
        // Shift-click: select range
        const start = Math.min(lastClickedIndex, index);
        const end = Math.max(lastClickedIndex, index);
        const rangeIds = tasks
          .slice(start, end + 1)
          .map((task) => task.id.toString());

        setSelectedRows((prev) => {
          const newSet = new Set(prev);
          rangeIds.forEach((id) => newSet.add(id));
          return newSet;
        });
      } else {
        // Normal click: toggle single item
        setSelectedRows((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(taskId)) {
            newSet.delete(taskId);
          } else {
            newSet.add(taskId);
          }
          return newSet;
        });
      }
      setLastClickedIndex(index);
    },
    [lastClickedIndex, tasks],
  );

  const handleToggleAll = React.useCallback(() => {
    if (selectedRows.size === tasks.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(tasks.map((task) => task.id.toString())));
    }
  }, [tasks, selectedRows.size]);

  const handleClearSelection = React.useCallback(() => {
    setSelectedRows(new Set());
    setLastClickedIndex(null);
  }, []);

  const handleOpenBulkLinkDialog = React.useCallback(() => {
    setIsBulkLinkDialogOpen(true);
  }, []);

  const handleCloseBulkLinkDialog = React.useCallback(() => {
    setIsBulkLinkDialogOpen(false);
  }, []);

  const handleBulkLinkSuccess = React.useCallback(() => {
    // Clear selection after successful bulk link
    handleClearSelection();
    // Could add a toast notification here if desired
  }, [handleClearSelection]);

  const handleOpenBulkCreateDialog = React.useCallback(() => {
    setIsBulkCreateDialogOpen(true);
  }, []);

  const handleCloseBulkCreateDialog = React.useCallback(() => {
    setIsBulkCreateDialogOpen(false);
  }, []);

  const handleBulkCreateSuccess = React.useCallback(
    (operationId: string) => {
      // Clear selection after starting bulk create
      handleClearSelection();
      // TODO: Show progress toast with operationId
      console.log('Bulk create operation started:', operationId);
    },
    [handleClearSelection],
  );

  // Handle saving current filter
  const handleSaveFilter = React.useCallback(
    async (name: string) => {
      await saveFilter(name, filters);
    },
    [saveFilter, filters],
  );

  // Handle loading a saved filter
  const handleLoadFilter = React.useCallback(
    (savedFilter: (typeof savedFilters)[number]) => {
      setAllFilters(savedFilter.filters);
    },
    [setAllFilters],
  );

  // Handle deleting a saved filter
  const handleDeleteFilter = React.useCallback(
    async (e: React.MouseEvent, filterId: string) => {
      e.stopPropagation();
      // eslint-disable-next-line no-alert, no-restricted-globals
      if (confirm('Are you sure you want to delete this saved filter?')) {
        await deleteFilter(filterId);
      }
    },
    [deleteFilter],
  );

  // Format saved date
  const formatSavedDate = React.useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }, []);

  // Handle adding search term
  const handleAddSearchTerm = React.useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      if (trimmedTerm && !searchTerms.includes(trimmedTerm)) {
        setSearchTerms((prev) => [...prev, trimmedTerm]);
      }
      setCurrentSearchInput('');
    },
    [searchTerms],
  );

  // Handle removing search term
  const handleRemoveSearchTerm = React.useCallback((term: string) => {
    setSearchTerms((prev) => prev.filter((t) => t !== term));
  }, []);

  // Handle search input key down
  const handleSearchKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        handleAddSearchTerm(currentSearchInput);
      } else if (
        e.key === 'Backspace' &&
        currentSearchInput === '' &&
        searchTerms.length > 0
      ) {
        // Remove last term when backspace on empty input
        setSearchTerms((prev) => prev.slice(0, -1));
      }
    },
    [currentSearchInput, handleAddSearchTerm, searchTerms.length],
  );

  // Handle paste to support comma-separated values
  const handleSearchPaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      if (pastedText.includes(',')) {
        e.preventDefault();
        const newTerms = pastedText
          .split(',')
          .map((term) => term.trim())
          .filter((term) => term && !searchTerms.includes(term));
        if (newTerms.length > 0) {
          setSearchTerms((prev) => [...prev, ...newTerms]);
          setCurrentSearchInput('');
        }
      }
    },
    [searchTerms],
  );

  // Assignee term handlers
  const handleAddAssigneeTerm = React.useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      if (trimmedTerm && !assigneeTerms.includes(trimmedTerm)) {
        setAssigneeTerms((prev) => [...prev, trimmedTerm]);
      }
      setCurrentAssigneeInput('');
    },
    [assigneeTerms],
  );

  const handleRemoveAssigneeTerm = React.useCallback((term: string) => {
    setAssigneeTerms((prev) => prev.filter((t) => t !== term));
  }, []);

  const handleAssigneeKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        handleAddAssigneeTerm(currentAssigneeInput);
      } else if (
        e.key === 'Backspace' &&
        currentAssigneeInput === '' &&
        assigneeTerms.length > 0
      ) {
        setAssigneeTerms((prev) => prev.slice(0, -1));
      }
    },
    [currentAssigneeInput, handleAddAssigneeTerm, assigneeTerms.length],
  );

  const handleAssigneePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      if (pastedText.includes(',')) {
        e.preventDefault();
        const newTerms = pastedText
          .split(',')
          .map((term) => term.trim())
          .filter((term) => term && !assigneeTerms.includes(term));
        if (newTerms.length > 0) {
          setAssigneeTerms((prev) => [...prev, ...newTerms]);
          setCurrentAssigneeInput('');
        }
      }
    },
    [assigneeTerms],
  );

  // Extract unique values for filters (status uses static constant EXTERNAL_TASK_STATUSES)
  const uniqueSources = React.useMemo<Option[]>(() => {
    const sources = new Set(tasks.map((task) => task.source));
    return Array.from(sources)
      .sort()
      .map((source) => ({ id: source, name: source }));
  }, [tasks]);

  // Get firms and matters from API filter options
  const uniqueFirms = React.useMemo<Option[]>(() => {
    return filterOptions?.firms || [];
  }, [filterOptions]);

  const uniqueMatters = React.useMemo<Option[]>(() => {
    return filterOptions?.matters || [];
  }, [filterOptions]);

  // Format time ago helper
  const formatTimeAgo = React.useCallback((dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return 'Just now';
  }, []);

  // Get status color from type-safe mapping
  const getStatusColor = React.useCallback((status: string) => {
    return EXTERNAL_TASK_STATUS_COLORS[status as ExternalTaskStatus] ?? 'zinc';
  }, []);

  // Sorting helpers
  const getDueDateSortState = React.useCallback(() => {
    if (ordering === 'due_date') return 'asc';
    if (ordering === '-due_date') return 'desc';
    return false;
  }, [ordering]);

  const toggleDueDateSorting = React.useCallback(() => {
    const currentSort = getDueDateSortState();
    if (currentSort === 'asc') {
      setOrdering('-due_date');
    } else if (currentSort === 'desc') {
      setOrdering(null);
    } else {
      setOrdering('due_date');
    }
  }, [getDueDateSortState]);

  const getLastSyncedSortState = React.useCallback(() => {
    if (ordering === 'last_synced_at') return 'asc';
    if (ordering === '-last_synced_at') return 'desc';
    return false;
  }, [ordering]);

  const toggleLastSyncedSorting = React.useCallback(() => {
    const currentSort = getLastSyncedSortState();
    if (currentSort === 'asc') {
      setOrdering('-last_synced_at');
    } else if (currentSort === 'desc') {
      setOrdering(null);
    } else {
      setOrdering('last_synced_at');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLastSyncedSortState]);

  const getLastActivitySortState = React.useCallback(() => {
    if (ordering === 'last_activity') return 'asc';
    if (ordering === '-last_activity') return 'desc';
    return false;
  }, [ordering]);

  const toggleLastActivitySorting = React.useCallback(() => {
    const currentSort = getLastActivitySortState();
    if (currentSort === 'asc') {
      setOrdering('-last_activity');
    } else if (currentSort === 'desc') {
      setOrdering(null);
    } else {
      setOrdering('last_activity');
    }
  }, [getLastActivitySortState, setOrdering]);

  // Define columns
  const columns = React.useMemo<ColumnDef<ExternalTaskListItemSchema>[]>(
    () => [
      {
        id: 'select',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const allSelected =
            tasks.length > 0 && selectedRows.size === tasks.length;
          const someSelected =
            selectedRows.size > 0 && selectedRows.size < tasks.length;

          return (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleToggleAll}
              className="ml-2"
            />
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <div
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleToggleRow(
                row.original.id.toString(),
                row.index,
                e.shiftKey,
              );
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleRow(
                  row.original.id.toString(),
                  row.index,
                  e.shiftKey,
                );
              }
            }}
            role="button"
            tabIndex={0}
          >
            <Checkbox
              checked={selectedRows.has(row.original.id.toString())}
              onChange={() => {
                // onChange is handled by the wrapper div's onClick
              }}
              className="ml-2"
            />
          </div>
        ),
        size: 50,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Task',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span
            className="truncate font-medium text-sm"
            title={row.original.name}
          >
            {row.original.name}
          </span>
        ),
        size: 280,
      },
      {
        id: 'needsUpdate',
        accessorKey: 'needs_update',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const isFiltered = needsUpdateFilter;
          return (
            <Dropdown>
              <DropdownButton
                as={Button}
                plain
                className={cn(
                  'flex items-center gap-1 font-medium text-xs',
                  isFiltered && 'text-blue-600 dark:text-blue-400',
                )}
              >
                Needs Update
                {isFiltered && (
                  <Badge color="blue" className="ml-1">
                    1
                  </Badge>
                )}
              </DropdownButton>

              <DropdownMenu
                className="min-w-[200px] p-2 block!"
                anchor="bottom start"
              >
                <DropdownItem
                  onClick={() => setNeedsUpdateFilter(!needsUpdateFilter)}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={needsUpdateFilter}
                    onChange={(checked: boolean) =>
                      setNeedsUpdateFilter(checked)
                    }
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Show only needs update</DropdownLabel>
                </DropdownItem>

                {isFiltered && (
                  <DropdownItem
                    onClick={() => setNeedsUpdateFilter(false)}
                    className="w-full flex! flex-row justify-center mt-2"
                  >
                    <X className="size-4" data-slot="icon" />
                    <DropdownLabel>Clear filter</DropdownLabel>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <div className="flex items-center justify-center w-full">
            {row.original.needs_update ? (
              <Badge color="orange">Yes</Badge>
            ) : (
              <span className="text-xs text-gray-400">-</span>
            )}
          </div>
        ),
        size: 120,
      },
      {
        id: 'status',
        accessorKey: 'status',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => (
          <ColumnHeaderFilter
            label="Status"
            options={EXTERNAL_TASK_STATUSES}
            selectedValues={statusFilter}
            onSelectionChange={setStatusFilter}
            multiSelect
          />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => {
          const statusOption = EXTERNAL_TASK_STATUSES.find(
            (s) => s.id === row.original.status,
          );
          return (
            <Badge color={getStatusColor(row.original.status)}>
              {statusOption?.name ?? row.original.status}
            </Badge>
          );
        },
        size: 120,
      },
      {
        id: 'dueDate',
        accessorKey: 'due_date',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const sorted = getDueDateSortState();
          return (
            <Button
              plain
              onClick={toggleDueDateSorting}
              className="flex items-center gap-1"
            >
              Due Date
              {sorted === 'asc' && <ChevronUp className="h-3 w-3" />}
              {sorted === 'desc' && <ChevronDown className="h-3 w-3" />}
              {!sorted && <ChevronsUpDown className="h-3 w-3 text-gray-400" />}
            </Button>
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span className="text-xs">{formatDate(row.original.due_date)}</span>
        ),
        size: 140,
      },
      {
        id: 'source',
        accessorKey: 'source',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => (
          <ColumnHeaderFilter
            label="Source"
            options={uniqueSources}
            selectedValues={sourceFilter}
            onSelectionChange={setSourceFilter}
            multiSelect
          />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span className="text-xs font-medium uppercase">
            {row.original.source}
          </span>
        ),
        size: 120,
      },
      {
        id: 'matter',
        accessorKey: 'matter_name',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => (
          <ColumnHeaderFilter
            label="Matter"
            options={uniqueMatters}
            selectedValues={matterFilter}
            onSelectionChange={setMatterFilter}
            multiSelect
          />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span
            className="text-xs font-medium truncate block"
            title={row.original.matter_name || 'Unknown'}
          >
            {row.original.matter_name || '-'}
          </span>
        ),
        size: 160,
      },
      {
        id: 'firm',
        accessorKey: 'firm_name',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => (
          <ColumnHeaderFilter
            label="Firm"
            options={uniqueFirms}
            selectedValues={firmFilter}
            onSelectionChange={setFirmFilter}
            multiSelect
          />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span
            className="text-xs font-medium truncate block"
            title={row.original.firm_name}
          >
            {row.original.firm_name}
          </span>
        ),
        size: 120,
      },
      {
        id: 'assignee',
        accessorKey: 'assignee',
        header: 'Assignee',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span className="text-xs truncate block">
            {row.original.assignee?.name || 'Unassigned'}
          </span>
        ),
        size: 140,
      },
      {
        id: 'lastSynced',
        accessorKey: 'last_synced_at',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const sorted = getLastSyncedSortState();
          return (
            <Button
              plain
              onClick={toggleLastSyncedSorting}
              className="flex items-center gap-1"
            >
              Last Synced
              {sorted === 'asc' && <ChevronUp className="h-3 w-3" />}
              {sorted === 'desc' && <ChevronDown className="h-3 w-3" />}
              {!sorted && <ChevronsUpDown className="h-3 w-3 text-gray-400" />}
            </Button>
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span className="text-xs text-gray-500">
            {formatTimeAgo(row.original.last_synced_at)}
          </span>
        ),
        size: 140,
      },
      {
        id: 'lastActivity',
        accessorKey: 'latest_activity',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const sorted = getLastActivitySortState();
          return (
            <Button
              plain
              onClick={toggleLastActivitySorting}
              className="flex items-center gap-1"
            >
              Last Activity
              {sorted === 'asc' && <ChevronUp className="h-3 w-3" />}
              {sorted === 'desc' && <ChevronDown className="h-3 w-3" />}
              {!sorted && <ChevronsUpDown className="h-3 w-3 text-gray-400" />}
            </Button>
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => {
          const activity = row.original.latest_activity;
          if (!activity) {
            return <span className="text-xs text-gray-400">-</span>;
          }

          // Truncate content to a snippet (first 50 characters)
          const snippet =
            activity.content.length > 50
              ? `${activity.content.substring(0, 50)}...`
              : activity.content;

          return (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(activity.external_created_at)}
                </span>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs font-medium text-gray-700">
                  {activity.creator?.name || 'Unknown'}
                </span>
              </div>
              <span
                className="text-xs text-gray-600 truncate block"
                title={activity.content}
              >
                {snippet}
              </span>
            </div>
          );
        },
        size: 240,
      },
      {
        id: 'linkedTasks',
        accessorKey: 'linked_tasks_count',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const isFiltered = linkedFilter !== 'all';
          return (
            <Dropdown>
              <DropdownButton
                as={Button}
                plain
                className={cn(
                  'flex items-center gap-1 font-medium text-xs',
                  isFiltered && 'text-blue-600 dark:text-blue-400',
                )}
              >
                Linked
                {isFiltered && (
                  <Badge color="blue" className="ml-1">
                    1
                  </Badge>
                )}
              </DropdownButton>

              <DropdownMenu
                className="min-w-[200px] p-2 block!"
                anchor="bottom start"
              >
                <DropdownItem
                  onClick={() => setLinkedFilter('all')}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={linkedFilter === 'all'}
                    onChange={() => setLinkedFilter('all')}
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>All tasks</DropdownLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() => setLinkedFilter('linked')}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={linkedFilter === 'linked'}
                    onChange={() => setLinkedFilter('linked')}
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Linked only</DropdownLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() => setLinkedFilter('unlinked')}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={linkedFilter === 'unlinked'}
                    onChange={() => setLinkedFilter('unlinked')}
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Unlinked only</DropdownLabel>
                </DropdownItem>

                {isFiltered && (
                  <DropdownItem
                    onClick={() => setLinkedFilter('all')}
                    className="w-full flex! flex-row justify-center mt-2"
                  >
                    <X className="size-4" data-slot="icon" />
                    <DropdownLabel>Clear filter</DropdownLabel>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <div className="flex items-center justify-center w-full">
            <span className="text-xs font-medium">
              {row.original.linked_tasks_count > 0
                ? row.original.linked_tasks_count
                : '-'}
            </span>
          </div>
        ),
        size: 80,
      },
      {
        id: 'ruleMatch',
        accessorKey: 'rule_evaluations',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const activeCount =
            (hasMatchedRuleFilter !== null ? 1 : 0) +
            (hideExcludedFilter ? 1 : 0);
          const isFiltered = activeCount > 0;
          return (
            <Dropdown>
              <DropdownButton
                as={Button}
                plain
                className={cn(
                  'flex items-center gap-1 font-medium text-xs',
                  isFiltered && 'text-blue-600 dark:text-blue-400',
                )}
              >
                Rule Match
                {isFiltered && (
                  <Badge color="blue" className="ml-1">
                    {activeCount}
                  </Badge>
                )}
              </DropdownButton>

              <DropdownMenu
                className="min-w-[200px] p-2 block!"
                anchor="bottom start"
              >
                <DropdownItem
                  onClick={() => setHasMatchedRuleFilter(null)}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={hasMatchedRuleFilter === null}
                    onChange={() => setHasMatchedRuleFilter(null)}
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>All tasks</DropdownLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() => setHasMatchedRuleFilter(true)}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={hasMatchedRuleFilter === true}
                    onChange={() => setHasMatchedRuleFilter(true)}
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Matched only</DropdownLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() => setHasMatchedRuleFilter(false)}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={hasMatchedRuleFilter === false}
                    onChange={() => setHasMatchedRuleFilter(false)}
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Unmatched only</DropdownLabel>
                </DropdownItem>

                <div className="my-1 border-t border-gray-200" />

                <DropdownItem
                  onClick={() => setHideExcludedFilter(!hideExcludedFilter)}
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={hideExcludedFilter}
                    onChange={(checked: boolean) =>
                      setHideExcludedFilter(checked)
                    }
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Hide excluded</DropdownLabel>
                </DropdownItem>

                {isFiltered && (
                  <DropdownItem
                    onClick={() => {
                      setHasMatchedRuleFilter(null);
                      setHideExcludedFilter(false);
                    }}
                    className="w-full flex! flex-row justify-center mt-2"
                  >
                    <X className="size-4" data-slot="icon" />
                    <DropdownLabel>Clear filter</DropdownLabel>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <RuleMatchCell
            evaluations={row.original.rule_evaluations}
            isExcluded={row.original.is_excluded}
            excludedByRuleName={row.original.excluded_by_rule_name}
            isExclusionPending={row.original.is_exclusion_pending}
            pendingExclusionRuleName={row.original.pending_exclusion_rule_name}
          />
        ),
        size: 120,
      },
      {
        id: 'slaStatus',
        accessorKey: 'rule_evaluations',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => {
          const isFiltered = slaStatusFilter.length > 0;
          return (
            <Dropdown>
              <DropdownButton
                as={Button}
                plain
                className={cn(
                  'flex items-center gap-1 font-medium text-xs',
                  isFiltered && 'text-blue-600 dark:text-blue-400',
                )}
              >
                SLA Status
                {isFiltered && (
                  <Badge color="blue" className="ml-1">
                    {slaStatusFilter.length}
                  </Badge>
                )}
              </DropdownButton>

              <DropdownMenu
                className="min-w-[200px] p-2 block!"
                anchor="bottom start"
              >
                <DropdownItem
                  onClick={() =>
                    setSlaStatusFilter(
                      slaStatusFilter.includes('on_time')
                        ? slaStatusFilter.filter((s) => s !== 'on_time')
                        : [...slaStatusFilter, 'on_time'],
                    )
                  }
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={slaStatusFilter.includes('on_time')}
                    onChange={() =>
                      setSlaStatusFilter(
                        slaStatusFilter.includes('on_time')
                          ? slaStatusFilter.filter((s) => s !== 'on_time')
                          : [...slaStatusFilter, 'on_time'],
                      )
                    }
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>On time</DropdownLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() =>
                    setSlaStatusFilter(
                      slaStatusFilter.includes('pending')
                        ? slaStatusFilter.filter((s) => s !== 'pending')
                        : [...slaStatusFilter, 'pending'],
                    )
                  }
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={slaStatusFilter.includes('pending')}
                    onChange={() =>
                      setSlaStatusFilter(
                        slaStatusFilter.includes('pending')
                          ? slaStatusFilter.filter((s) => s !== 'pending')
                          : [...slaStatusFilter, 'pending'],
                      )
                    }
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Pending</DropdownLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() =>
                    setSlaStatusFilter(
                      slaStatusFilter.includes('late')
                        ? slaStatusFilter.filter((s) => s !== 'late')
                        : [...slaStatusFilter, 'late'],
                    )
                  }
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={slaStatusFilter.includes('late')}
                    onChange={() =>
                      setSlaStatusFilter(
                        slaStatusFilter.includes('late')
                          ? slaStatusFilter.filter((s) => s !== 'late')
                          : [...slaStatusFilter, 'late'],
                      )
                    }
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>Late</DropdownLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() =>
                    setSlaStatusFilter(
                      slaStatusFilter.includes('no_sla')
                        ? slaStatusFilter.filter((s) => s !== 'no_sla')
                        : [...slaStatusFilter, 'no_sla'],
                    )
                  }
                  className="w-full flex! flex-row items-center gap-2"
                >
                  <Checkbox
                    checked={slaStatusFilter.includes('no_sla')}
                    onChange={() =>
                      setSlaStatusFilter(
                        slaStatusFilter.includes('no_sla')
                          ? slaStatusFilter.filter((s) => s !== 'no_sla')
                          : [...slaStatusFilter, 'no_sla'],
                      )
                    }
                    className="w-4 h-4 shrink-0"
                  />
                  <DropdownLabel>No SLA</DropdownLabel>
                </DropdownItem>

                {isFiltered && (
                  <DropdownItem
                    onClick={() => setSlaStatusFilter([])}
                    className="w-full flex! flex-row justify-center mt-2"
                  >
                    <X className="size-4" data-slot="icon" />
                    <DropdownLabel>Clear filter</DropdownLabel>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          );
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <SlaStatusCell
            evaluations={row.original.rule_evaluations}
            isExcluded={row.original.is_excluded}
            isExclusionPending={row.original.is_exclusion_pending}
          />
        ),
        size: 120,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tasks,
      selectedRows,
      handleToggleAll,
      handleToggleRow,
      formatTimeAgo,
      getStatusColor,
      uniqueSources,
      uniqueFirms,
      uniqueMatters,
      statusFilter,
      sourceFilter,
      firmFilter,
      matterFilter,
      assigneeFilter,
      needsUpdateFilter,
      linkedFilter,
      hasMatchedRuleFilter,
      slaStatusFilter,
      getDueDateSortState,
      toggleDueDateSorting,
      getLastSyncedSortState,
      toggleLastSyncedSorting,
      getLastActivitySortState,
      toggleLastActivitySorting,
      setStatusFilter,
      setSourceFilter,
      setFirmFilter,
      setMatterFilter,
      setNeedsUpdateFilter,
      setLinkedFilter,
      setHasMatchedRuleFilter,
      setSlaStatusFilter,
    ],
  );

  const table = useReactTable({
    data: tasks,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 45,
    overscan: 20,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  // Handle infinite scroll - automatically fetch next page when near the end
  React.useEffect(() => {
    const lastVirtualRow = virtualRows[virtualRows.length - 1];
    if (!lastVirtualRow) return;

    const bufferSize = 20;
    const nearEndOfLoadedData =
      lastVirtualRow.index >= tasks.length - bufferSize;

    // Fetch next page if we're near the end and not already fetching
    if (
      nearEndOfLoadedData &&
      tasks.length < totalCount &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    virtualRows,
    tasks.length,
    totalCount,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // Clear selection when filters change
  React.useEffect(() => {
    setSelectedRows(new Set());
    setLastClickedIndex(null);
  }, [
    debouncedSearch,
    statusFilter,
    sourceFilter,
    firmFilter,
    matterFilter,
    debouncedAssigneeFilter,
    needsUpdateFilter,
    ordering,
  ]);

  // Check if any filters are active
  const hasActiveFilters =
    statusFilter.length > 0 ||
    sourceFilter.length > 0 ||
    firmFilter.length > 0 ||
    matterFilter.length > 0 ||
    assigneeFilter !== '' ||
    needsUpdateFilter ||
    linkedFilter !== 'all' ||
    ordering !== null ||
    searchInput !== '' ||
    lastActivityAfter !== '' ||
    lastActivityBefore !== '' ||
    hasMatchedRuleFilter !== null ||
    hideExcludedFilter ||
    slaStatusFilter.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Search Bar */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between gap-3">
          {/* Filter Inputs Container */}
          <div className="flex items-center gap-3">
            {/* Multi-term search input */}
            <div className="flex-1 max-w-lg min-w-md">
              <div className="relative border border-slate-300 rounded-md px-2 py-1.5 flex flex-wrap items-center gap-1.5 min-h-[38px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
                {searchTerms.map((term) => (
                  <Badge
                    key={term}
                    color="blue"
                    className="flex items-center gap-1 px-2 py-0.5"
                  >
                    <span className="text-xs">{term}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSearchTerm(term)}
                      className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <input
                  type="text"
                  value={currentSearchInput}
                  onChange={(e) => setCurrentSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onPaste={handleSearchPaste}
                  placeholder={
                    searchTerms.length === 0
                      ? 'Search external tasks... (Enter or comma to add multiple terms)'
                      : 'Add another term...'
                  }
                  className="flex-1 min-w-[200px] outline-none border-none text-sm bg-transparent"
                />
              </div>
            </div>

            {/* Multi-term assignee input */}
            <div className="flex-1 max-w-lg min-w-sm">
              <div className="relative border border-slate-300 rounded-md px-2 py-1.5 flex flex-wrap items-center gap-1.5 min-h-[38px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
                {assigneeTerms.map((term) => (
                  <Badge
                    key={term}
                    color="purple"
                    className="flex items-center gap-1 px-2 py-0.5"
                  >
                    <span className="text-xs">{term}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAssigneeTerm(term)}
                      className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <input
                  type="text"
                  value={currentAssigneeInput}
                  onChange={(e) => setCurrentAssigneeInput(e.target.value)}
                  onKeyDown={handleAssigneeKeyDown}
                  onPaste={handleAssigneePaste}
                  placeholder={
                    assigneeTerms.length === 0
                      ? 'Filter by assignee... (Enter or comma to add multiple)'
                      : 'Add another assignee...'
                  }
                  className="flex-1 min-w-[120px] outline-none border-none text-sm bg-transparent"
                />
              </div>
            </div>

            {/* Last Activity Date Filters */}
            <div className="flex items-center gap-1.5">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="text-xs text-gray-600 font-medium whitespace-nowrap">
                Last Activity:
              </label>
              <Input
                type="date"
                value={lastActivityAfter}
                onChange={(e) => setLastActivityAfter(e.target.value)}
                placeholder="After..."
                className="w-[140px]"
                title="Show tasks with last activity after this date"
              />
              <span className="text-xs text-gray-500">to</span>
              <Input
                type="date"
                value={lastActivityBefore}
                onChange={(e) => setLastActivityBefore(e.target.value)}
                placeholder="Before..."
                className="w-[140px]"
                title="Show tasks with last activity before this date"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Saved Filters Dropdown */}
            <ShadcnDropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button outline className="text-sm" title="Load a saved filter">
                  <Bookmark className="h-4 w-4 mr-1" />
                  Saved Filters
                  {savedFilters.length > 0 && (
                    <Badge color="blue" className="ml-1">
                      {savedFilters.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[280px]">
                {savedFilters.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No saved filters yet
                  </div>
                ) : (
                  savedFilters.map((savedFilter) => (
                    <DropdownMenuItem
                      key={savedFilter.id}
                      onClick={() => handleLoadFilter(savedFilter)}
                      className="flex items-center justify-between gap-2 cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {savedFilter.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatSavedDate(savedFilter.savedAt)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteFilter(e, savedFilter.id)}
                        className="shrink-0 text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                        title="Delete this filter"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </ShadcnDropdownMenu>

            {/* Save Filter Button */}
            {hasActiveFilters && (
              <Button
                outline
                onClick={() => setIsSaveFilterDialogOpen(true)}
                className="text-sm"
                title="Save current filter settings"
              >
                <Save className="h-4 w-4 mr-1" />
                Save Filter
              </Button>
            )}

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                plain
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearAllFilters();
                }}
                className="text-sm"
                title="Remove all filters and show all tasks"
              >
                <X className="h-4 w-4 mr-1" />
                Clear all filters
              </Button>
            )}

            <div className="text-sm text-gray-500">
              {totalCount} task{totalCount !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedRows.size > 0 && (
        <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedRows.size} task{selectedRows.size !== 1 ? 's' : ''}{' '}
              selected
            </span>
            <Button
              plain
              onClick={handleClearSelection}
              className="text-sm text-blue-700 hover:text-blue-900"
            >
              Clear selection
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button outline onClick={handleOpenBulkLinkDialog}>
              Link to Tasks
            </Button>
            <Button color="blue" onClick={handleOpenBulkCreateDialog}>
              Create Internal Tasks
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div
        ref={tableContainerRef}
        className="overflow-auto relative"
        style={{ height: 'calc(100vh - 220px)' }}
      >
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
              <p className="text-sm text-gray-600">Loading external tasks...</p>
            </div>
          </div>
        )}

        <table className="w-full table-fixed">
          <thead className="sticky top-0 bg-white border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="text-left px-2 py-1.5 font-semibold text-gray-700 text-xs bg-white"
                    style={{ width: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];

              // Placeholder for rows not yet loaded
              if (!row) {
                return (
                  <tr
                    key={`placeholder-${virtualRow.index}`}
                    className="h-[45px]"
                  >
                    {columns.map((_, colIndex) => (
                      <td key={colIndex} className="px-2 py-1">
                        <div className="animate-pulse bg-gray-200 h-4 rounded" />
                      </td>
                    ))}
                  </tr>
                );
              }

              return (
                <tr
                  key={row.id}
                  onClick={() => handleRowClick(row.original)}
                  className={cn(
                    'border-b hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer transition-colors h-[45px]',
                    selectedTaskId === row.original.id.toString() &&
                      'bg-blue-50 dark:bg-blue-900/20',
                    selectedRows.has(row.original.id.toString()) &&
                      'bg-blue-50/50 dark:bg-blue-900/10',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-2 py-1 overflow-hidden"
                      style={{ width: `${cell.column.getSize()}px` }}
                    >
                      <div className="flex items-center h-full">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>

        {/* Empty state */}
        {!isLoading && tasks.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-500">No external tasks found</p>
              {searchInput && (
                <p className="text-sm text-gray-400 mt-1">
                  Try adjusting your search
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* External Task Detail Modal */}
      {selectedTaskId && (
        <ExternalTaskDetail
          externalTaskId={selectedTaskId}
          onClose={handleCloseDetail}
        />
      )}

      {/* Bulk Link Dialog */}
      <BulkLinkDialog
        isOpen={isBulkLinkDialogOpen}
        onClose={handleCloseBulkLinkDialog}
        externalTaskIds={Array.from(selectedRows)}
        onSuccess={handleBulkLinkSuccess}
      />

      {/* Bulk Create Dialog */}
      <BulkCreateDialog
        isOpen={isBulkCreateDialogOpen}
        onClose={handleCloseBulkCreateDialog}
        externalTaskIds={Array.from(selectedRows)}
        onSuccess={handleBulkCreateSuccess}
      />

      {/* Save Filter Dialog */}
      <SaveFilterDialog
        isOpen={isSaveFilterDialogOpen}
        onClose={() => setIsSaveFilterDialogOpen(false)}
        onSave={handleSaveFilter}
      />
    </div>
  );
};
